/**
 * Simple Analytics Tracking
 * 
 * ÉVÉNEMENTS TRACKÉS:
 * - place_view: Ouverture d'une fiche lieu
 * - place_click_directions: Clic sur le bouton "Itinéraire"
 * - place_click_call: Clic sur le bouton "Appeler"
 * 
 * COMMENT VÉRIFIER:
 * 1. Aller dans Admin > Analytics
 * 2. Regarder la section "Derniers événements reçus"
 * 3. L'événement doit apparaître en moins de 30 secondes
 */

const EDGE_FUNCTION_URL = 'https://rlwgmtlygzdpdhbiyibl.supabase.co/functions/v1/track-event-simple';

// Session ID - unique par onglet
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('simple_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem('simple_session_id', sessionId);
  }
  return sessionId;
};

// User ID from localStorage (set by auth)
const getUserId = (): string | null => {
  try {
    const authData = localStorage.getItem('sb-rlwgmtlygzdpdhbiyibl-auth-token');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed?.user?.id || null;
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
};

type SimpleEventName = 
  | 'place_view' 
  | 'place_click_directions' 
  | 'place_click_call' 
  | 'place_click_website'
  | 'place_click_share'
  | 'pwa_install_prompt_shown'
  | 'pwa_install_accepted'
  | 'pwa_install_dismissed'
  | 'pwa_install_never_ask';

interface SimpleEvent {
  event_name: SimpleEventName;
  place_id: string | null;
  timestamp: string;
  session_id: string;
  user_id: string | null;
  metadata?: Record<string, unknown>;
}

/**
 * Envoie un événement IMMÉDIATEMENT (pas de batching)
 * Utilise sendBeacon pour garantir l'envoi même si l'utilisateur quitte
 */
function sendEventNow(event: SimpleEvent): void {
  const payload = JSON.stringify({ events: [event] });
  
  // Debug log
  console.log('[Analytics] Sending event:', event.event_name, 'for place:', event.place_id);
  
  // Essayer sendBeacon d'abord (survit aux navigations)
  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    const success = navigator.sendBeacon(EDGE_FUNCTION_URL, blob);
    if (success) {
      console.log('[Analytics] ✅ Event sent via sendBeacon');
      return;
    }
  }
  
  // Fallback: fetch avec keepalive
  fetch(EDGE_FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true, // Important: survit aux navigations
  }).then(() => {
    console.log('[Analytics] ✅ Event sent via fetch');
  }).catch((err) => {
    console.error('[Analytics] ❌ Failed to send event:', err);
  });
}

/**
 * Track l'ouverture d'une fiche lieu
 * Déclenché: À l'affichage du composant PlaceDetail
 */
export function trackPlaceView(placeId: string): void {
  if (!placeId) {
    console.warn('[Analytics] trackPlaceView called without placeId');
    return;
  }
  
  sendEventNow({
    event_name: 'place_view',
    place_id: placeId,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    user_id: getUserId(),
  });
}

/**
 * Track le clic sur "Itinéraire"
 * IMPORTANT: Appeler AVANT la redirection vers Google Maps
 */
export function trackDirectionsClick(placeId: string): void {
  if (!placeId) {
    console.warn('[Analytics] trackDirectionsClick called without placeId');
    return;
  }
  
  sendEventNow({
    event_name: 'place_click_directions',
    place_id: placeId,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    user_id: getUserId(),
  });
}

/**
 * Track le clic sur "Appeler"
 * IMPORTANT: Appeler AVANT le lien tel:
 */
export function trackCallClick(placeId: string): void {
  if (!placeId) {
    console.warn('[Analytics] trackCallClick called without placeId');
    return;
  }
  
  sendEventNow({
    event_name: 'place_click_call',
    place_id: placeId,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    user_id: getUserId(),
  });
}

/**
 * Track le clic sur "Site Web"
 * IMPORTANT: Appeler AVANT l'ouverture du lien
 */
export function trackWebsiteClick(placeId: string): void {
  if (!placeId) {
    console.warn('[Analytics] trackWebsiteClick called without placeId');
    return;
  }
  
  sendEventNow({
    event_name: 'place_click_website',
    place_id: placeId,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    user_id: getUserId(),
  });
}

/**
 * Track le clic sur "Partager"
 * Enregistre la méthode utilisée (native ou clipboard)
 */
export function trackShareClick(placeId: string, method: 'native' | 'clipboard'): void {
  if (!placeId) {
    console.warn('[Analytics] trackShareClick called without placeId');
    return;
  }
  
  sendEventNow({
    event_name: 'place_click_share',
    place_id: placeId,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    user_id: getUserId(),
    metadata: { method },
  });
}

/**
 * Track les événements d'installation PWA
 */
export function trackPWAInstall(
  action: 'prompt_shown' | 'accepted' | 'dismissed' | 'never_ask',
  platform: 'ios' | 'android' | 'desktop'
): void {
  const eventName = `pwa_install_${action}` as SimpleEventName;
  
  sendEventNow({
    event_name: eventName,
    place_id: null,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    user_id: getUserId(),
    metadata: {
      platform,
      is_standalone: window.matchMedia('(display-mode: standalone)').matches,
    },
  });
  
  console.log('[Analytics] PWA event:', eventName, 'platform:', platform);
}
